/**
 * Ancoragem em erro nomeado.
 *
 * ESTA E A PONTE COM OS 637 TASK-MODELS. O catalogo ja exige que cada distrator
 * corresponda a UM erro especifico e documentavel -- nunca a um numero aleatorio.
 * Sem que fosse essa a intencao, o catalogo ja esta escrito na forma de uma
 * especificacao de gerador: cada "logica dos 4 distratores" e uma lista de
 * solucionadores defeituosos.
 *
 * O reuso e o ativo: sobre 85 task-models a expectativa e convergir para ~120-180
 * ancoras distintas, muito reaproveitadas. E o reuso que torna possivel a analitica
 * transversal ("este aluno dispara MED.AREA_POR_PERIMETRO em 7 templates diferentes").
 */

import type { RichText } from './richtext.ts';
import type { SkillCode } from './taxonomy.ts';

/** As cinco familias, verbatim da secao 2.2 do catalogo de sintese. */
export type ErrorFamily =
  | 'procedimento' // (a) erro de procedimento/algoritmo
  | 'concepcao' // (b) concepcao equivocada tipica do objeto de conhecimento
  | 'leitura-suporte' // (c) erro de leitura do suporte (escala, linha/coluna, marca)
  | 'parada-precoce' // (d) resposta de uma etapa intermediaria
  | 'formula-correlata' // (e) area<->perimetro, r<->d, media<->mediana, seno<->tangente
  /** Exclusiva de N3/N4: nao e um erro de calculo, e um erro de construcao. */
  | 'construcao';

export const ERROR_FAMILY_LABEL: Readonly<Record<ErrorFamily, string>> = {
  procedimento: 'Erro de procedimento ou algoritmo',
  concepcao: 'Concepção equivocada do objeto de conhecimento',
  'leitura-suporte': 'Erro de leitura do suporte',
  'parada-precoce': 'Parada precoce (etapa intermediária)',
  'formula-correlata': 'Uso de fórmula ou regra correlata',
  construcao: 'Erro de construção geométrica',
};

export type ErrorCode = string & { readonly __error: unique symbol };
export const errorCode = (s: string): ErrorCode => s as ErrorCode;

export interface ErrorAnchor {
  readonly code: ErrorCode;
  readonly family: ErrorFamily;
  /** Rotulo curto, mostrado ao professor nos relatorios. */
  readonly label: string;
  /** O que o aluno fez, em uma frase. Vai para o feedback e para a analitica. */
  readonly description: string;
  /** Para quais habilidades esta concepcao equivocada e diagnostica. */
  readonly skills: readonly SkillCode[];
  /** Sugestao de remediacao; opcional nesta fase. */
  readonly remediation?: RichText;
}

const anchors: ErrorAnchor[] = [];

function define(
  code: string,
  family: ErrorFamily,
  label: string,
  description: string,
  skills: readonly string[] = [],
): ErrorCode {
  const c = errorCode(code);
  anchors.push({
    code: c,
    family,
    label,
    description,
    skills: skills as readonly SkillCode[],
  });
  return c;
}

// ---------------------------------------------------------------------------
// Estatistica -- medidas de tendencia central (H1.36, H3.30, H3.31)
// ---------------------------------------------------------------------------

export const EST_SEM_PONDERAR = define(
  'EST.SEM_PONDERAR',
  'concepcao',
  'Média simples, sem ponderar',
  'Calculou a média simples dos valores, ignorando quantas vezes cada um aparece.',
  ['H3.30', 'H1.36'],
);

export const EST_DIVIDE_POR_CLASSES = define(
  'EST.DIVIDE_POR_CLASSES',
  'procedimento',
  'Divide pelo número de colunas',
  'Somou corretamente os produtos, mas dividiu pelo número de colunas em vez do total de dados.',
  ['H3.30'],
);

export const EST_SOMA_FREQUENCIAS = define(
  'EST.SOMA_FREQUENCIAS',
  'procedimento',
  'Soma as frequências ao numerador',
  'Somou as quantidades ao numerador, em vez de usá-las só como pesos dos valores.',
  ['H3.30'],
);

export const EST_MEDIA_POR_MEDIANA = define(
  'EST.MEDIA_POR_MEDIANA',
  'formula-correlata',
  'Confunde média com mediana',
  'Devolveu a classe (ou o valor) central em vez da média ponderada.',
  ['H1.36', 'H3.31'],
);

export const EST_PONTO_MEDIO_EXTREMOS = define(
  'EST.PONTO_MEDIO_EXTREMOS',
  'formula-correlata',
  'Tomou o meio entre o menor e o maior valor',
  'Fez a média só do menor e do maior valor, ignorando os demais dados.',
  ['H1.36'],
);

export const EST_MODA_POR_MEDIA = define(
  'EST.MODA_POR_MEDIA',
  'formula-correlata',
  'Confunde média com moda',
  'Devolveu o valor que mais se repete, em vez da média.',
  ['H1.36', 'H3.31'],
);

export const EST_MEDIA_DAS_ALTURAS = define(
  'EST.MEDIA_DAS_ALTURAS',
  'leitura-suporte',
  'Fez a média das alturas das colunas',
  'Calculou a média das quantidades — as alturas das colunas — em vez da média dos valores que elas contam.',
  ['H3.30'],
);

export const EST_CENTRO_DA_ESCALA = define(
  'EST.CENTRO_DA_ESCALA',
  'leitura-suporte',
  'Tomou o meio da escala como média',
  'Respondeu o ponto central da escala — o meio da régua —, sem olhar para onde estão os dados.',
  ['H1.36'],
);

// ---------------------------------------------------------------------------
// Estatistica -- grafico de setores (H1.34, H3.29)
//
// Um grafico de setores tem uma propriedade que nenhum outro tem: a parte se le no
// ANGULO. Por isso os erros daqui sao todos sobre qual e o inteiro -- 360, 180 ou 100 --
// e sobre confundir o dado com a resposta.
// ---------------------------------------------------------------------------

export const SET_ANGULO_COMO_PCT = define(
  'SET.ANGULO_COMO_PCT',
  'leitura-suporte',
  'Leu o ângulo como porcentagem',
  'Tomou a medida do ângulo, em graus, como se fosse a porcentagem da fatia.',
  ['H1.34', 'H3.29'],
);

export const SET_MEIA_VOLTA = define(
  'SET.MEIA_VOLTA',
  'concepcao',
  'Usou 180° como o círculo inteiro',
  'Dividiu o ângulo por 180 em vez de 360: meia volta no lugar da volta completa.',
  ['H1.34', 'H3.29'],
);

export const SET_DIVIDE_POR_100 = define(
  'SET.DIVIDE_POR_100',
  'parada-precoce',
  'Parou na porcentagem',
  'Chegou à fração do círculo e respondeu a porcentagem, sem voltar à quantidade pedida.',
  ['H1.34', 'H3.29'],
);

export const SET_COMPLEMENTO = define(
  'SET.COMPLEMENTO',
  'procedimento',
  'Respondeu o que sobra',
  'Deu o total menos a fatia perguntada — o resto do gráfico, e não a fatia.',
  ['H1.34', 'H3.29'],
);

export const SET_RESPONDE_O_ANGULO = define(
  'SET.RESPONDE_O_ANGULO',
  'parada-precoce',
  'Respondeu a medida do ângulo',
  'Devolveu o ângulo em graus, que era o dado do enunciado, e não a quantidade pedida.',
  ['H1.34', 'H3.29'],
);

export const SET_FATIA_VIZINHA = define(
  'SET.FATIA_VIZINHA',
  'leitura-suporte',
  'Leu a fatia ao lado',
  'Respondeu pelo setor vizinho, e não pelo setor sobre o qual se perguntou.',
  ['H1.34', 'H3.29'],
);

export const SET_TOTAL_POR_FATIA = define(
  'SET.TOTAL_POR_FATIA',
  'concepcao',
  'Dividiu o total pelo número de fatias',
  'Repartiu o total em partes iguais, como se as fatias tivessem todas o mesmo tamanho.',
  ['H1.34', 'H3.29'],
);

// ---------------------------------------------------------------------------
// Estatistica -- MODIFICAR uma distribuicao para ela ter a media pedida (H3.33)
//
// Eixo cognitivo III. Aqui o aluno nao calcula a media de dados dados: ele mexe nos
// dados ate a media ser a pedida -- e os erros sao de outra natureza, todos sobre a
// relacao entre uma coluna e o conjunto.
// ---------------------------------------------------------------------------

export const EST_MOVEU_PELA_DIFERENCA = define(
  'EST.MOVEU_PELA_DIFERENCA',
  'concepcao',
  'Mudou a coluna pela diferença das médias',
  'Subiu (ou desceu) a coluna exatamente o que a média precisa subir, esquecendo que a mudança se reparte entre todas as colunas.',
  ['H3.33'],
);

export const EST_COLUNA_VIRA_A_MEDIA = define(
  'EST.COLUNA_VIRA_A_MEDIA',
  'concepcao',
  'Levou a coluna ao valor da média',
  'Pôs a coluna na altura da média pedida, como se cada coluna precisasse valer a média.',
  ['H3.33'],
);

export const EST_ALVO_NA_SOMA = define(
  'EST.ALVO_NA_SOMA',
  'concepcao',
  'Fez a soma valer o alvo',
  'Ajustou a coluna para que a SOMA das colunas desse o valor pedido, e não a média.',
  ['H3.33'],
);

export const EST_DIVIDE_SEM_A_COLUNA = define(
  'EST.DIVIDE_SEM_A_COLUNA',
  'procedimento',
  'Deixou a própria coluna fora da conta',
  'Dividiu pelo número das outras colunas, sem contar a coluna que está sendo mudada.',
  ['H3.33'],
);

export const EST_SENTIDO_INVERTIDO = define(
  'EST.SENTIDO_INVERTIDO',
  'procedimento',
  'Mudou para o lado contrário',
  'Acertou o tamanho da mudança, mas subiu a coluna onde era para descer (ou o contrário).',
  ['H3.33'],
);

// ---------------------------------------------------------------------------
// Grandezas e medidas -- velocidade media (H3.25)
// ---------------------------------------------------------------------------

export const VEL_MEDIA_ARITMETICA = define(
  'VEL.MEDIA_ARITMETICA',
  'concepcao',
  'Média aritmética das velocidades',
  'Tirou a média aritmética das duas velocidades, ignorando que os tempos são diferentes.',
  ['H3.25'],
);

export const VEL_SOMA_VELOCIDADES = define(
  'VEL.SOMA_VELOCIDADES',
  'procedimento',
  'Soma as velocidades',
  'Somou as duas velocidades como se velocidades fossem aditivas.',
  ['H3.25'],
);

export const VEL_UM_TRECHO_SO = define(
  'VEL.UM_TRECHO_SO',
  'parada-precoce',
  'Usa a distância de um trecho só',
  'Dividiu a distância de apenas um dos trechos pelo tempo total.',
  ['H3.25'],
);

export const VEL_TEMPO_TROCADO = define(
  'VEL.TEMPO_TROCADO',
  'procedimento',
  'Calcula o tempo com a velocidade do outro trecho',
  'Trocou as velocidades ao calcular os tempos de cada trecho.',
  ['H3.25'],
);

export const VEL_DISTANCIA_TOTAL_POR_TEMPO_PARCIAL = define(
  'VEL.DIST_TOTAL_TEMPO_PARCIAL',
  'parada-precoce',
  'Distância total sobre o tempo de um trecho',
  'Dividiu a distância total pelo tempo de apenas um dos trechos.',
  ['H3.25'],
);

export const VEL_SEGUNDO_TRECHO_SO = define(
  'VEL.SEGUNDO_TRECHO_SO',
  'parada-precoce',
  'Usa a distância do segundo trecho só',
  'Dividiu a distância do segundo trecho pelo tempo total.',
  ['H3.25'],
);

export const VEL_MENOR_VELOCIDADE = define(
  'VEL.MENOR_VELOCIDADE',
  'concepcao',
  'Responde a menor das velocidades',
  'Concluiu que a média do percurso é a velocidade do trecho mais lento.',
  ['H3.25'],
);

export const VEL_HARMONICA_INDEVIDA = define(
  'VEL.HARMONICA_INDEVIDA',
  'formula-correlata',
  'Aplica a média harmônica sem as distâncias serem iguais',
  'Usou 2v₁v₂/(v₁+v₂), que só vale quando os dois trechos têm a mesma distância.',
  ['H3.25'],
);

export const VEL_HARMONICA_INVERTIDA = define(
  'VEL.HARMONICA_INVERTIDA',
  'procedimento',
  'Inverte a média harmônica',
  'Aplicou a fórmula da média harmônica de cabeça para baixo.',
  ['H3.25'],
);

export const VEL_PONDERA_POR_DISTANCIA = define(
  'VEL.PONDERA_POR_DISTANCIA',
  'concepcao',
  'Pondera as velocidades pelas distâncias',
  'Fez a média das velocidades ponderada pelas distâncias; a ponderação correta é pelos tempos.',
  ['H3.25'],
);

export const VEL_TEMPO_POR_DISTANCIA = define(
  'VEL.TEMPO_POR_DISTANCIA',
  'formula-correlata',
  'Divide tempo por distância',
  'Inverteu a definição: calculou tempo ÷ distância em vez de distância ÷ tempo.',
  ['H3.25'],
);

// ---------------------------------------------------------------------------
// Construcao geometrica (N4) -- namespace CONSTR
// ---------------------------------------------------------------------------

export const CONSTR_ESTIMADO_A_OLHO = define(
  'CONSTR.ESTIMADO_A_OLHO',
  'construcao',
  'Estimou a olho',
  'O objeto está visualmente no lugar certo, mas não foi construído: não se mantém quando a figura se move.',
);

export const CONSTR_MEDIATRIZ_POR_BISSETRIZ = define(
  'CONSTR.MEDIATRIZ_POR_BISSETRIZ',
  'construcao',
  'Construiu a mediatriz de AB',
  'Construiu a mediatriz do segmento AB em vez da bissetriz do ângulo AOB.',
  ['H1.15'],
);

export const CONSTR_MEDIANA_POR_BISSETRIZ = define(
  'CONSTR.MEDIANA_POR_BISSETRIZ',
  'construcao',
  'Construiu a mediana',
  'Ligou o vértice ao ponto médio de AB — é a mediana, que só coincide com a bissetriz no triângulo isósceles.',
  ['H1.15'],
);

export const CONSTR_BISSETRIZ_EXTERNA = define(
  'CONSTR.BISSETRIZ_EXTERNA',
  'construcao',
  'Construiu a bissetriz externa',
  'Bissetou o ângulo suplementar: a semirreta é perpendicular à bissetriz interna.',
  ['H1.15'],
);

export const CONSTR_INCOMPLETO = define(
  'CONSTR.INCOMPLETO',
  'construcao',
  'Construção incompleta',
  'A construção parou antes do fim: o objeto pedido não chegou a ser traçado.',
);

export const CONSTR_SEM_LADOS = define(
  'CONSTR.SEM_LADOS',
  'construcao',
  'Faltam os lados',
  'Os vértices da figura estão construídos, mas nem todos os lados foram traçados.',
);

export const CONSTR_OBJETO_ERRADO = define(
  'CONSTR.OBJETO_ERRADO',
  'construcao',
  'Objeto de tipo errado',
  'Produziu um objeto de outro tipo (uma circunferência onde se pedia uma semirreta, por exemplo).',
);

// ---------------------------------------------------------------------------
// Matematica financeira -- incidencia percentual em cadeia (H2.4)
// ---------------------------------------------------------------------------

export const PCT_SOMA_TAXAS = define(
  'PCT.SOMA_TAXAS',
  'concepcao',
  'Soma os percentuais de reajuste',
  'Tratou dois reajustes de 10% e 5% como um único de 15%; taxas em cadeia se multiplicam, não se somam.',
  ['H2.4'],
);

export const PCT_SOBRE_INICIAL = define(
  'PCT.SOBRE_INICIAL',
  'procedimento',
  'Aplica todos os reajustes sobre o valor inicial',
  'Calculou cada reajuste sobre o valor original, ignorando que o segundo incide sobre o já atualizado.',
  ['H2.4'],
);

export const PCT_SO_O_MAIOR = define(
  'PCT.SO_O_MAIOR',
  'parada-precoce',
  'Aplica só o maior reajuste',
  'Considerou que o reajuste maior absorve o menor e aplicou apenas ele.',
  ['H2.4'],
);

export const PCT_SO_O_PRIMEIRO = define(
  'PCT.SO_O_PRIMEIRO',
  'parada-precoce',
  'Para no primeiro reajuste',
  'Aplicou apenas o primeiro reajuste e apresentou o resultado intermediário como final.',
  ['H2.4'],
);

export const PCT_SINAL_TROCADO = define(
  'PCT.SINAL_TROCADO',
  'procedimento',
  'Troca acréscimo por desconto',
  'Inverteu o sentido de um dos reajustes: aplicou desconto onde havia aumento (ou o contrário).',
  ['H2.4'],
);

export const PCT_MEDIA_DAS_TAXAS = define(
  'PCT.MEDIA_DAS_TAXAS',
  'formula-correlata',
  'Usa a média das duas taxas',
  'Substituiu os dois reajustes por um único, igual à média aritmética deles.',
  ['H2.4'],
);

export const PCT_VARIACAO_POR_MONTANTE = define(
  'PCT.VARIACAO_POR_MONTANTE',
  'concepcao',
  'Responde a variação em vez do montante',
  'Calculou corretamente quanto o valor aumentou, mas a pergunta era pelo valor final.',
  ['H2.4', 'H1.4'],
);

// ---------------------------------------------------------------------------
// Funcao afim: lei <-> grafico (H1.8)
// ---------------------------------------------------------------------------

export const FUN_SINAL_ANGULAR = define(
  'FUN.SINAL_ANGULAR',
  'concepcao',
  'Sinal do coeficiente angular trocado',
  'Confundiu crescente com decrescente: leu o sinal de a ao contrário.',
  ['H1.8'],
);

export const FUN_INTERCEPTO_EM_X = define(
  'FUN.INTERCEPTO_EM_X',
  'leitura-suporte',
  'Lê o intercepto no eixo x',
  'Tomou o ponto em que a reta cruza o eixo x como se fosse o coeficiente linear.',
  ['H1.8'],
);

export const FUN_ANGULAR_UM = define(
  'FUN.ANGULAR_UM',
  'concepcao',
  'Assume coeficiente angular igual a 1',
  'Acertou o intercepto, mas desenhou a inclinação como se a fosse 1 (ou −1).',
  ['H1.8'],
);

export const FUN_PELA_ORIGEM = define(
  'FUN.PELA_ORIGEM',
  'concepcao',
  'Trata a função afim como linear',
  'Ignorou o coeficiente linear e fez a reta passar pela origem.',
  ['H1.8'],
);

export const FUN_TROCA_A_B = define(
  'FUN.TROCA_A_B',
  'procedimento',
  'Troca o coeficiente angular pelo linear',
  'Usou a no lugar de b e b no lugar de a.',
  ['H1.8'],
);

export const FUN_REFLETE_EIXO_X = define(
  'FUN.REFLETE_EIXO_X',
  'procedimento',
  'Reflete a reta no eixo x',
  'Trocou o sinal dos dois coeficientes, refletindo o gráfico inteiro.',
  ['H1.8'],
);

// ---------------------------------------------------------------------------
// Contagem: diagonais e apertos de mao (H2.20)
// ---------------------------------------------------------------------------

export const CNT_NAO_SUBTRAI_LADOS = define(
  'CNT.NAO_SUBTRAI_LADOS',
  'parada-precoce',
  'Não subtrai os lados',
  'Contou C(n,2) — todos os segmentos entre vértices — sem descontar os n lados do polígono.',
  ['H2.20'],
);

export const CNT_SEM_DIVIDIR = define(
  'CNT.SEM_DIVIDIR',
  'procedimento',
  'Não divide por 2',
  'Contou cada diagonal duas vezes, uma por extremidade, e esqueceu de dividir por 2.',
  ['H2.20'],
);

export const CNT_ARRANJO = define(
  'CNT.ARRANJO',
  'formula-correlata',
  'Usa arranjo onde cabe combinação',
  'Usou n(n−1): tratou os pares como ordenados, quando a diagonal AB é a mesma que BA.',
  ['H2.20'],
);

export const CNT_UM_VERTICE = define(
  'CNT.UM_VERTICE',
  'parada-precoce',
  'Conta as diagonais de um vértice só',
  'Calculou n−3, que é quantas diagonais saem de UM vértice, e parou aí.',
  ['H2.20'],
);

export const CNT_CONTA_LADOS = define(
  'CNT.CONTA_LADOS',
  'concepcao',
  'Confunde diagonal com lado',
  'Devolveu o número de lados do polígono.',
  ['H2.20'],
);

export const CNT_ESQUECE_O_PROPRIO = define(
  'CNT.ESQUECE_O_PROPRIO',
  'concepcao',
  'Desconta só os dois vizinhos',
  'Usou n(n−2)/2: descontou os dois vértices vizinhos, mas esqueceu que o próprio vértice também não conta.',
  ['H2.20'],
);

export const CNT_SUBTRAI_LADOS_DUAS_VEZES = define(
  'CNT.SUBTRAI_LADOS_DUAS_VEZES',
  'procedimento',
  'Subtrai os lados duas vezes',
  'Descontou os n lados de C(n,2) e depois descontou os mesmos n lados outra vez.',
  ['H2.20'],
);

export const CNT_DOBRO_LADOS = define(
  'CNT.DOBRO_LADOS',
  'procedimento',
  'Usa o dobro do número de lados',
  'Multiplicou o número de lados por 2, sem base na estrutura do problema.',
  ['H2.20'],
);

// ---------------------------------------------------------------------------
// Marcacao na malha e construcao de quadrilateros (namespace K)
// ---------------------------------------------------------------------------

export const MALHA_INCLUI_FORA = define(
  'MALHA.INCLUI_FORA',
  'construcao',
  'Marcou pontos fora da figura',
  'Alguns pontos marcados estão fora da figura.',
);

export const MALHA_SO_FRONTEIRA = define(
  'MALHA.SO_FRONTEIRA',
  'construcao',
  'Marcou só o contorno',
  'Marcou apenas os pontos sobre o contorno, esquecendo o interior da figura.',
);

export const MALHA_IGNORA_FRONTEIRA = define(
  'MALHA.IGNORA_FRONTEIRA',
  'construcao',
  'Excluiu o contorno',
  'Marcou o interior, mas deixou de fora os pontos que caem sobre o contorno.',
);

export const MALHA_ESCALA_IGNORADA = define(
  'MALHA.ESCALA_IGNORADA',
  'leitura-suporte',
  'Marcou pontos que não são de coordenadas inteiras',
  'Tratou todo cruzamento da malha como resposta. Cada quadradinho mede menos que 1 unidade, então só alguns cruzamentos têm as duas coordenadas inteiras — e só esses contam.',
  ['H3.7'],
);

export const MALHA_INCOMPLETO = define(
  'MALHA.INCOMPLETO',
  'construcao',
  'Faltam pontos',
  'A figura contém pontos de coordenadas inteiras que não foram marcados.',
);

export const CONSTR_PARALELOGRAMO = define(
  'CONSTR.PARALELOGRAMO',
  'construcao',
  'Construiu um paralelogramo',
  'Os dois pares de lados opostos ficaram paralelos: é paralelogramo (ou retângulo), não trapézio.',
  ['H1.15'],
);

export const CONSTR_TRAPEZIO_SEM_RETO = define(
  'CONSTR.TRAPEZIO_SEM_RETO',
  'construcao',
  'Trapézio sem ângulo reto',
  'Construiu um trapézio válido, mas sem os dois ângulos retos exigidos.',
  ['H1.15'],
);

export const CONSTR_SEM_PARALELAS = define(
  'CONSTR.SEM_PARALELAS',
  'construcao',
  'Nenhum par de lados paralelos',
  'O quadrilátero construído não tem lados opostos paralelos, então não é trapézio.',
  ['H1.15'],
);

// ---------------------------------------------------------------------------
// Genericos, reutilizaveis por muitos templates
// ---------------------------------------------------------------------------

export const ARRED_TRUNCA = define(
  'ARRED.TRUNCA',
  'procedimento',
  'Trunca em vez de arredondar',
  'Descartou as casas excedentes em vez de arredondar.',
);

export const ARRED_CASA_ERRADA = define(
  'ARRED.CASA_ERRADA',
  'procedimento',
  'Arredonda na casa errada',
  'Arredondou em uma ordem decimal diferente da pedida.',
);

export const PROC_PARADA_ETAPA_1 = define(
  'PROC.PARADA_ETAPA_1',
  'parada-precoce',
  'Parou na primeira etapa',
  'Apresentou o resultado correto de uma etapa intermediária como se fosse a resposta final.',
);

// ---------------------------------------------------------------------------
// Quadrado no plano cartesiano (H3.11.K01)
// ---------------------------------------------------------------------------

export const QUAD_RETANGULO = define(
  'QUAD.RETANGULO',
  'concepcao',
  'Formou um retângulo, não um quadrado',
  'Os ângulos ficaram retos, mas os lados não ficaram todos iguais: a figura é um retângulo. Num quadrado, além dos ângulos retos, os quatro lados têm a mesma medida.',
  ['H3.11'],
);

export const QUAD_LOSANGO = define(
  'QUAD.LOSANGO',
  'concepcao',
  'Formou um losango, não um quadrado',
  'Os quatro lados ficaram iguais, mas os ângulos não são retos: a figura é um losango.',
  ['H3.11'],
);

export const QUAD_DIAGONAL_COMO_LADO = define(
  'QUAD.DIAGONAL_COMO_LADO',
  'leitura-suporte',
  'Tratou a diagonal como lado',
  'Os dois vértices dados são opostos — o segmento entre eles é uma diagonal —, mas o quadrado foi construído como se eles fossem vizinhos.',
  ['H3.11'],
);

export const QUAD_LADO_COMO_DIAGONAL = define(
  'QUAD.LADO_COMO_DIAGONAL',
  'leitura-suporte',
  'Tratou o lado como diagonal',
  'Os dois vértices dados são vizinhos — o segmento entre eles é um lado —, mas o quadrado foi construído como se eles fossem opostos.',
  ['H3.11'],
);

export const QUAD_NAO_QUADRADO = define(
  'QUAD.NAO_QUADRADO',
  'construcao',
  'Os quatro pontos não formam um quadrado',
  'Com os pontos marcados, os quatro vértices não formam um quadrado: os lados não são todos iguais e os ângulos não são todos retos.',
  ['H3.11'],
);

export const QUAD_QUANTIDADE = define(
  'QUAD.QUANTIDADE',
  'construcao',
  'Marcou um número de pontos diferente de dois',
  'Faltavam exatamente dois vértices, e foi marcado um número diferente de dois pontos.',
  ['H3.11'],
);

// ---------------------------------------------------------------------------
// Simetria em relação a um eixo (H3.18.K01, H3.20.K01)
// ---------------------------------------------------------------------------

export const SIM_TRANSLACAO = define(
  'SIM.TRANSLACAO',
  'concepcao',
  'Deslocou a figura sem espelhar',
  'A figura foi copiada para o outro lado do eixo com a mesma orientação — isso é uma translação. Na simetria, cada ponto vai para o lado oposto do eixo, à mesma distância, e a figura fica espelhada.',
  ['H3.18', 'H3.20'],
);

export const SIM_EIXO_ERRADO = define(
  'SIM.EIXO_ERRADO',
  'leitura-suporte',
  'Espelhou em relação a outra reta',
  'A figura foi espelhada, mas em relação a uma reta diferente do eixo indicado.',
  ['H3.18', 'H3.20'],
);

export const SIM_DISTANCIA = define(
  'SIM.DISTANCIA',
  'procedimento',
  'Distância ao eixo diferente da original',
  'Os pontos foram para o outro lado do eixo, na direção perpendicular a ele, mas não ficaram à mesma distância do eixo que os pontos originais.',
  ['H3.18', 'H3.20'],
);

export const SIM_INCOMPLETO = define(
  'SIM.INCOMPLETO',
  'construcao',
  'Faltam vértices da figura simétrica',
  'Parte dos vértices da figura simétrica está no lugar certo, mas nem todos foram marcados.',
  ['H3.18', 'H3.20'],
);

export const SIM_PONTOS_A_MAIS = define(
  'SIM.PONTOS_A_MAIS',
  'construcao',
  'Marcou pontos que não são simétricos',
  'Há pontos marcados que não correspondem ao simétrico de nenhum vértice da figura.',
  ['H3.18', 'H3.20'],
);

// ---------------------------------------------------------------------------
// Ângulos de polígonos (H2.13.K01)
// ---------------------------------------------------------------------------

export const ANG_SEM_SUBTRAIR = define(
  'ANG.SEM_SUBTRAIR',
  'procedimento',
  'Contou um triângulo por lado',
  'Multiplicou 180° pelo número de lados, como se o polígono se dividisse em um triângulo por lado. Traçando as diagonais de um único vértice, ele se divide em n − 2 triângulos.',
  ['H2.13'],
);

export const ANG_N_MENOS_1 = define(
  'ANG.N_MENOS_1',
  'procedimento',
  'Contou n − 1 triângulos',
  'Considerou n − 1 triângulos na divisão do polígono; traçando as diagonais de um vértice, são n − 2.',
  ['H2.13'],
);

export const ANG_N_MENOS_3 = define(
  'ANG.N_MENOS_3',
  'concepcao',
  'Usou o número de diagonais de um vértice',
  'Usou n − 3, o número de diagonais que saem de um vértice, no lugar de n − 2, o número de triângulos que essas diagonais formam.',
  ['H2.13'],
);

export const ANG_EXT_COMO_INT = define(
  'ANG.EXT_COMO_INT',
  'formula-correlata',
  'Confundiu ângulos externos com internos',
  'Respondeu sobre os ângulos internos quando a pergunta era sobre os externos.',
  ['H2.13'],
);

export const ANG_INT_COMO_EXT = define(
  'ANG.INT_COMO_EXT',
  'formula-correlata',
  'Confundiu ângulos internos com externos',
  'Respondeu sobre os ângulos externos quando a pergunta era sobre os internos.',
  ['H2.13'],
);

export const ANG_SOMA_PARES = define(
  'ANG.SOMA_PARES',
  'concepcao',
  'Somou internos e externos',
  'Em cada vértice, o ângulo interno e o externo somam 180°; somar isso em todos os vértices dá n × 180°, que não é a soma dos ângulos externos.',
  ['H2.13'],
);

export const ANG_TRIANGULO = define(
  'ANG.TRIANGULO',
  'concepcao',
  'Usou 180°, a soma do triângulo',
  'Tomou 180° — a soma dos ângulos internos de um triângulo — como se valesse para qualquer polígono.',
  ['H2.13'],
);

export const ANG_DOIS_EXTERNOS = define(
  'ANG.DOIS_EXTERNOS',
  'concepcao',
  'Contou dois ângulos externos por vértice',
  'Em cada vértice há dois ângulos externos iguais (opostos pelo vértice), e a soma considera só um por vértice. Contar os dois dá duas voltas completas.',
  ['H2.13'],
);

export const ANG_SOMA_POR_UM = define(
  'ANG.SOMA_POR_UM',
  'parada-precoce',
  'Deu a soma em vez de um ângulo',
  'Calculou a soma de todos os ângulos, mas a pergunta era a medida de cada um: faltou dividir pelo número de ângulos.',
  ['H2.13'],
);

export const ANG_DIVIDIU_180 = define(
  'ANG.DIVIDIU_180',
  'procedimento',
  'Dividiu 180° pelo número de lados',
  'Dividiu 180° pelo número de lados, em vez de dividir a soma correta dos ângulos.',
  ['H2.13'],
);

// ---------------------------------------------------------------------------
// Gráfico com seletor (H3.30.K01)
// ---------------------------------------------------------------------------

export const GRAF_IGUAL_VIZINHO = define(
  'GRAF.IGUAL_VIZINHO',
  'concepcao',
  'Valor igual ao de um mês vizinho',
  'Escolheu um valor igual ao de um mês vizinho. Valores iguais não mostram crescimento nem queda: a sequência precisa mudar de um mês para o outro.',
  ['H3.30', 'H1.9'],
);

export const GRAF_QUEBRA_TENDENCIA = define(
  'GRAF.QUEBRA_TENDENCIA',
  'leitura-suporte',
  'Quebrou a tendência do gráfico',
  'O valor escolhido faz a sequência mudar de sentido ao passar por esse mês: ela sobe e depois desce (ou desce e depois sobe).',
  ['H3.30', 'H1.9'],
);

export const GRAF_PA_DIFERENCA_DOBRADA = define(
  'GRAF.PA_DIFERENCA_DOBRADA',
  'procedimento',
  'Aplicou o aumento duas vezes',
  'O aumento de um mês para o outro é sempre o mesmo, e o valor escolhido aplicou esse aumento duas vezes seguidas.',
  ['H1.12', 'H3.30'],
);

export const GRAF_PA_DIFERENCA_ERRADA = define(
  'GRAF.PA_DIFERENCA_ERRADA',
  'leitura-suporte',
  'Usou um aumento diferente do gráfico',
  'Usou como aumento mensal um valor que não é a diferença entre dois meses vizinhos do gráfico.',
  ['H1.12', 'H3.30'],
);

export const GRAF_PA_MEDIA = define(
  'GRAF.PA_MEDIA',
  'formula-correlata',
  'Usou a média dos outros meses',
  'Escolheu a média dos meses conhecidos, que não segue o aumento constante de um mês para o outro.',
  ['H1.12', 'H3.30'],
);

// ---------------------------------------------------------------------------
// Volume de uma caixa pela planificação (H1.19.K01)
// ---------------------------------------------------------------------------

export const VOL_SEM_ESCALA = define(
  'VOL.SEM_ESCALA',
  'leitura-suporte',
  'Não converteu os quadradinhos em centímetros',
  'Multiplicou as medidas contadas em quadradinhos, sem levar em conta que cada quadradinho tem um tamanho em centímetros.',
  ['H1.19', 'H1.25'],
);

export const VOL_ESCALA_UMA_VEZ = define(
  'VOL.ESCALA_UMA_VEZ',
  'procedimento',
  'Converteu a escala uma vez só',
  'Multiplicou o volume em quadradinhos pelo lado do quadradinho uma única vez. São três medidas, e cada uma precisa ser convertida.',
  ['H1.19', 'H1.25'],
);

export const VOL_ESCALA_AO_QUADRADO = define(
  'VOL.ESCALA_AO_QUADRADO',
  'formula-correlata',
  'Usou o fator de área na conversão',
  'Multiplicou o volume em quadradinhos pelo quadrado do lado do quadradinho — o fator que converte áreas —, e não pelo cubo, que converte volumes.',
  ['H1.19', 'H1.25'],
);

export const VOL_AREA_TOTAL = define(
  'VOL.AREA_TOTAL',
  'formula-correlata',
  'Calculou a área da planificação',
  'Somou as áreas das seis faces: essa é a área total da superfície da caixa, e não o seu volume.',
  ['H1.19', 'H1.25'],
);

export const VOL_AREA_BASE = define(
  'VOL.AREA_BASE',
  'parada-precoce',
  'Calculou só a área da base',
  'Multiplicou o comprimento pela largura, mas não multiplicou pela altura.',
  ['H1.19', 'H1.25'],
);

export const VOL_SOMA_ARESTAS = define(
  'VOL.SOMA_ARESTAS',
  'formula-correlata',
  'Somou as arestas',
  'Somou os comprimentos das doze arestas da caixa, o que mede o contorno dela, e não o espaço interno.',
  ['H1.19', 'H1.25'],
);

export const VOL_PLANIFICACAO_COMO_ARESTA = define(
  'VOL.PLANIFICACAO_COMO_ARESTA',
  'leitura-suporte',
  'Mediu várias faces enfileiradas como uma aresta',
  'Contou, na planificação, o comprimento da base somado ao das duas faces laterais, como se fosse uma única aresta da caixa.',
  ['H1.19'],
);

export const VOL_ALTURA_COM_TAMPA = define(
  'VOL.ALTURA_COM_TAMPA',
  'leitura-suporte',
  'Contou a tampa junto com a altura',
  'Na planificação, a face de trás e a tampa ficam em sequência; a altura foi medida somando as duas, e não só a face de trás.',
  ['H1.19'],
);

// ---------------------------------------------------------------------------
// Coordenadas e retângulos (H3.11.K01–K03)
// ---------------------------------------------------------------------------

export const COORD_TROCADAS = define(
  'COORD.TROCADAS',
  'leitura-suporte',
  'Trocou abscissa e ordenada',
  'Os pontos certos foram escritos com as coordenadas invertidas. No par ordenado, o primeiro número é a abscissa (x) e o segundo, a ordenada (y).',
  ['H3.11', 'H3.7'],
);

export const COORD_MISTURADAS = define(
  'COORD.MISTURADAS',
  'procedimento',
  'Combinou coordenadas do mesmo eixo',
  'Cada vértice novo foi formado com as duas abscissas (ou as duas ordenadas) dos pontos dados. Um vértice novo aproveita a abscissa de um ponto e a ordenada do outro.',
  ['H3.11'],
);

export const QUAD_GIRO_ERRADO = define(
  'QUAD.GIRO_ERRADO',
  'procedimento',
  'Girou a meia diagonal sem mudar o sinal',
  'Para girar um deslocamento (a, b) em 90°, trocam-se as coordenadas e muda-se o sinal de uma delas: (−b, a). Só trocar dá (b, a), e os quatro pontos formam um retângulo que não é quadrado.',
  ['H3.11'],
);

export const RET_FEZ_QUADRADO = define(
  'RET.FEZ_QUADRADO',
  'leitura-suporte',
  'Formou um quadrado inclinado, e não o retângulo pedido',
  'Os pontos formam um quadrado com a diagonal dada, mas o retângulo pedido tem os lados paralelos aos eixos.',
  ['H3.11'],
);

export const RET_ERRADO = define(
  'RET.ERRADO',
  'construcao',
  'Os quatro pontos não formam o retângulo pedido',
  'Com os pontos marcados, os quatro vértices não formam um retângulo de lados paralelos aos eixos que tenha os pontos dados como vértices opostos.',
  ['H3.11'],
);

export const SIM_CORRESPONDENCIA = define(
  'SIM.CORRESPONDENCIA',
  'concepcao',
  'Trocou a correspondência dos vértices',
  "O triângulo ficou no lugar certo, mas com os vértices trocados: A' tem de ser o simétrico de A, B' o de B e C' o de C.",
  ['H3.18'],
);

export const ANG_EXT_RETO = define(
  'ANG.EXT_RETO',
  'concepcao',
  'Tomou cada ângulo externo como reto',
  'Considerou um giro de 90° em cada vértice, como num retângulo. Só no retângulo os ângulos externos são retos; nos outros polígonos eles se ajustam, e a soma é sempre uma volta completa.',
  ['H2.13'],
);

// ---------------------------------------------------------------------------
// Encontre o erro (K-erro): onde o aluno aponta, e os erros das resoluções
// ---------------------------------------------------------------------------

export const ERRO_HERDADO = define(
  'ERRO.HERDADO',
  'leitura-suporte',
  'Apontou um passo que só herdou o erro',
  'O passo marcado tem um valor errado, mas a conta dele, a partir do passo anterior, está certa: ele apenas carrega o erro de antes. O primeiro erro está num passo anterior.',
  ['H1.6'],
);

export const ERRO_PASSO_CERTO = define(
  'ERRO.PASSO_CERTO',
  'leitura-suporte',
  'Apontou um passo que está certo',
  'O passo marcado está correto; o primeiro erro da resolução aparece depois dele.',
  ['H1.6'],
);

export const EQ_DISTRIBUTIVA = define(
  'EQ.DISTRIBUTIVA',
  'procedimento',
  'Distribuiu só para o primeiro termo',
  'O número que multiplica os parênteses multiplica todos os termos de dentro, e não só o primeiro.',
  ['H1.10', 'H1.6'],
);

export const EQ_SINAL = define(
  'EQ.SINAL',
  'procedimento',
  'Mudou o termo de lado sem inverter a operação',
  'Um termo que está somando de um lado passa para o outro subtraindo (e vice-versa): o que se faz é somar ou subtrair o mesmo valor dos dois lados da igualdade.',
  ['H1.10', 'H1.6'],
);

export const EQ_DIVISAO = define(
  'EQ.DIVISAO',
  'procedimento',
  'Desfez a multiplicação subtraindo',
  'Para isolar a incógnita que está multiplicada por um número, dividem-se os dois lados por esse número; subtraí-lo não desfaz a multiplicação.',
  ['H1.10', 'H1.6'],
);

export const ARIT_CONTA = define(
  'ARIT.CONTA',
  'procedimento',
  'Errou uma conta',
  'O raciocínio do passo está certo, mas a operação foi feita com erro.',
  ['H1.6', 'H1.3'],
);

export const ANG_DIAGONAIS_ERRADAS = define(
  'ANG.DIAGONAIS_ERRADAS',
  'procedimento',
  'Contou errado as diagonais de um vértice',
  'De um vértice saem n − 3 diagonais: o vértice não se liga a si mesmo nem aos dois vizinhos, que já estão ligados a ele pelos lados.',
  ['H2.13'],
);

export const ANG_TRIANGULO_360 = define(
  'ANG.TRIANGULO_360',
  'concepcao',
  'Usou 360° como soma dos ângulos do triângulo',
  'A soma dos ângulos internos de um triângulo é 180°; 360° é a soma dos ângulos de um quadrilátero.',
  ['H2.13'],
);

export const PCT_TAXA_COMO_FATOR = define(
  'PCT.TAXA_COMO_FATOR',
  'procedimento',
  'Usou a taxa no lugar do fator',
  'Multiplicar por d/100 dá o valor do desconto, e não o preço depois dele. O fator de um desconto de d% é 1 − d/100.',
  ['H1.6', 'H2.4'],
);

// ---------------------------------------------------------------------------
// Reta numérica (H1.1.K01)
// ---------------------------------------------------------------------------

export const RETA_NEGATIVO_PARTE = define(
  'RETA.NEGATIVO_PARTE',
  'concepcao',
  'Andou a parte decimal para o lado errado',
  'Num número negativo como −1,5, a parte "0,5" também é negativa: a partir do −1, anda-se para a ESQUERDA. Andar para a direita leva a −0,5.',
  ['H1.1'],
);

export const RETA_SINAL = define(
  'RETA.SINAL',
  'leitura-suporte',
  'Trocou o sinal do número',
  'O marcador foi colocado no simétrico do número: à direita do zero em vez de à esquerda, ou o contrário.',
  ['H1.1'],
);

export const RETA_CONTAGEM = define(
  'RETA.CONTAGEM',
  'procedimento',
  'Contou as marcas em vez dos intervalos',
  'Cada parte da unidade é um INTERVALO entre duas marcas. Contar a marca de partida como a primeira desloca o ponto em uma parte.',
  ['H1.1'],
);

export const RETA_NUMERADOR = define(
  'RETA.NUMERADOR',
  'concepcao',
  'Marcou o numerador como se fosse o número',
  'Uma fração a/b é a partes de tamanho 1/b, e não o número a. Por exemplo, 3/4 fica entre 0 e 1, e não no 3.',
  ['H1.1'],
);

// ---------------------------------------------------------------------------
// Montar um gráfico de colunas (H3.30.K02)
// ---------------------------------------------------------------------------

export const GRAF_TROCOU_COLUNAS = define(
  'GRAF.TROCOU_COLUNAS',
  'leitura-suporte',
  'Trocou os valores entre as colunas',
  'As alturas estão certas, mas em colunas trocadas: cada valor da tabela vai na coluna da sua categoria.',
  ['H3.30'],
);

export const GRAF_LEITURA_ESCALA = define(
  'GRAF.LEITURA_ESCALA',
  'leitura-suporte',
  'Leu a escala do eixo errado',
  'As colunas guardam as proporções da tabela, mas a altura de todas foi multiplicada pelo mesmo fator: a escala do eixo vertical foi lida errado.',
  ['H3.30'],
);

/* --- leitura crítica de gráfico: o eixo que não começa em zero (H1.37) --- */

export const GRAF_EIXO_TRUNCADO = define(
  'GRAF.EIXO_TRUNCADO',
  'leitura-suporte',
  'Comparou as alturas num eixo truncado',
  'Comparou as colunas pela altura desenhada, sem reparar que o eixo vertical não começa em zero: o corte na base exagera a diferença.',
  ['H1.37'],
);

export const GRAF_CONTAGEM_LINHAS = define(
  'GRAF.CONTAGEM_LINHAS',
  'procedimento',
  'Errou a contagem das linhas de grade',
  'Algumas colunas pararam uma linha de grade acima ou abaixo do valor: entre dois números do eixo, cada linha vale o passo da grade.',
  ['H3.30'],
);

// ---------------------------------------------------------------------------
// Ordenar números reais (H1.1.K02)
// ---------------------------------------------------------------------------

export const ORD_NEGATIVOS = define(
  'ORD.NEGATIVOS',
  'concepcao',
  'Ordenou os negativos pelo módulo',
  'Entre dois negativos, o menor é o que está mais longe do zero: −1,4 é menor que −0,6, embora 1,4 seja maior que 0,6.',
  ['H1.1'],
);

export const ORD_DECIMAL_TAMANHO = define(
  'ORD.DECIMAL_TAMANHO',
  'concepcao',
  'Comparou decimais pelo número de algarismos',
  'O número de algarismos depois da vírgula não diz qual decimal é maior: compara-se casa a casa, a partir dos décimos. 0,45 é menor que 0,6, porque 4 décimos é menos que 6 décimos.',
  ['H1.1'],
);

export const ORD_FRACAO_DENOMINADOR = define(
  'ORD.FRACAO_DENOMINADOR',
  'concepcao',
  'Achou que denominador maior dá fração maior',
  'Com o mesmo numerador, quanto maior o denominador, menores são as partes: 1/5 é menor que 1/3.',
  ['H1.1'],
);

export const ORD_INVERTIDA = define(
  'ORD.INVERTIDA',
  'leitura-suporte',
  'Ordenou ao contrário',
  'A ordem está invertida: foi pedida a ordem crescente, do menor para o maior.',
  ['H1.1'],
);

// ---------------------------------------------------------------------------
// Expressões algébricas a partir de uma tabela (H1.12.K02)
// ---------------------------------------------------------------------------

export const EXPR_SEM_MENOS_UM = define(
  'EXPR.SEM_MENOS_UM',
  'procedimento',
  'Esqueceu o "− 1" do termo geral',
  'Do primeiro termo até o de posição n, soma-se a razão n − 1 vezes, e não n vezes: aₙ = a₁ + (n − 1)·r.',
  ['H1.12', 'H1.7'],
);

export const EXPR_SEM_TERMO_INICIAL = define(
  'EXPR.SEM_TERMO_INICIAL',
  'parada-precoce',
  'Esqueceu o termo que não depende da variável',
  'A expressão capta o quanto aumenta a cada passo, mas esquece de onde a sequência (ou a função) começa.',
  ['H1.12', 'H1.7'],
);

export const EXPR_TROCOU_COEFICIENTES = define(
  'EXPR.TROCOU_COEFICIENTES',
  'leitura-suporte',
  'Trocou os papéis dos números',
  'O número que acompanha a variável é o que se soma a cada passo; o outro é o ponto de partida. Na expressão, os dois ficaram trocados.',
  ['H1.12', 'H1.7'],
);

export const EXPR_SOMOU_POSICAO = define(
  'EXPR.SOMOU_POSICAO',
  'concepcao',
  'Somou o aumento à posição, em vez de multiplicar',
  'O aumento se repete a cada passo: depois de n passos, ele foi somado n vezes — o que se escreve multiplicando pela variável, e não somando.',
  ['H1.12', 'H1.7'],
);

export const EXPR_INCLINACAO_ERRADA = define(
  'EXPR.INCLINACAO_ERRADA',
  'leitura-suporte',
  'Tomou um valor da tabela como taxa de variação',
  'A taxa de variação é a DIFERENÇA entre dois valores seguidos da tabela, e não um dos valores.',
  ['H1.7', 'H1.8'],
);

export const EXPR_CONSTANTE = define(
  'EXPR.CONSTANTE',
  'concepcao',
  'Escreveu uma expressão sem a variável',
  'Sem a variável, a expressão dá sempre o mesmo número — e a tabela muda de uma linha para a outra.',
  ['H1.12', 'H1.7'],
);

// ---------------------------------------------------------------------------
// Ajustar a reta pelos controles (H1.8.K02)
// ---------------------------------------------------------------------------

export const FUN_AJUSTE_PARALELA = define(
  'FUN.AJUSTE_PARALELA',
  'procedimento',
  'Acertou a inclinação, mas não o deslocamento',
  'A reta ficou paralela à pedida: sobe (ou desce) no ritmo certo, mas corta o eixo y no lugar errado. O b é a altura em que a reta cruza o eixo y.',
  ['H1.8'],
);

export const FUN_AJUSTE_SO_CORTE = define(
  'FUN.AJUSTE_SO_CORTE',
  'concepcao',
  'Acertou o corte no eixo y, mas não a inclinação',
  'A reta cruza o eixo y na altura certa, mas sobe (ou desce) num ritmo diferente. O a é quanto y varia quando x aumenta 1.',
  ['H1.8'],
);

export const FUN_AJUSTE_UM_PONTO = define(
  'FUN.AJUSTE_UM_PONTO',
  'parada-precoce',
  'Fez a reta passar por só um dos pontos',
  'A reta encosta num dos pontos e erra o outro. Com dois pontos, os dois coeficientes precisam ser acertados juntos.',
  ['H1.8'],
);

// ---------------------------------------------------------------------------
// Algebra -- a balanca de dois pratos (H1.10, H1.6)
//
// Resolver uma equacao mexendo nos pratos. Os erros daqui nao sao de conta: sao de
// PROCEDIMENTO, e cada um e um jeito de quebrar a igualdade ou de parar antes dela
// dizer o que se queria saber.
//
// "RECIPIENTE", E NAO "SACO". O rotulo e a descricao chegam ao aluno como retorno, e
// cada contexto do item chama o objeto de um jeito — saco, pote, saquinho. O texto
// daqui e compartilhado e nao sabe qual; dizia "saco" a quem estava mexendo em potes.
// Os CODIGOS ficam como estao: estao gravados nas tentativas ja registradas.
// ---------------------------------------------------------------------------

export const BAL_UM_LADO_SO = define(
  'BAL.UM_LADO_SO',
  'procedimento',
  'Terminou com a balança torta',
  'O que saiu de um prato não pesa o mesmo que o que saiu do outro. Os dois pratos valiam o mesmo e deixaram de valer: uma balança torta não está calibrada.',
  ['H1.10', 'H1.6'],
);

export const BAL_PAROU_ANTES_DE_REPARTIR = define(
  'BAL.PAROU_ANTES_DE_REPARTIR',
  'parada-precoce',
  'Parou com vários recipientes num prato',
  'Chegou a "vários recipientes de um lado, pesos do outro" e parou. Isso diz quanto pesam TODOS os recipientes juntos; falta repartir os dois pratos para saber quanto pesa um.',
  ['H1.10', 'H1.6'],
);

export const BAL_SOBROU_PESO = define(
  'BAL.SOBROU_PESO',
  'parada-precoce',
  'Deixou peso junto do recipiente',
  'O prato do recipiente ainda tem pesos. Enquanto houver peso do lado dele, a balança compara o recipiente MAIS alguma coisa, e não o recipiente sozinho.',
  ['H1.10', 'H1.6'],
);

export const BAL_SEM_SACO = define(
  'BAL.SEM_SACO',
  'procedimento',
  'Tirou todos os recipientes',
  'Não sobrou recipiente nenhum nos pratos. Sem ele, a balança não tem mais o que revelar: o que se procura é justamente quanto ele pesa.',
  ['H1.10', 'H1.6'],
);

// ---------------------------------------------------------------------------
// Geometria -- pavimentar o plano com um poligono regular (H2.15)
//
// Eixo cognitivo III. Um poligono regular ladrilha o plano sozinho quando o seu angulo
// interno cabe um numero INTEIRO de vezes em 360 graus. Os erros daqui sao todos sobre
// trocar essa condicao por outra coisa: o numero de lados, a existencia de angulo reto,
// ou a intuicao de que "regular" ja basta.
// ---------------------------------------------------------------------------

export const LADR_PENTAGONO = define(
  'LADR.PENTAGONO',
  'concepcao',
  'Marcou o pentágono regular',
  'O ângulo interno do pentágono regular é 108°, e 360 ÷ 108 não dá um número inteiro: três pentágonos deixam uma fresta de 36°.',
  ['H2.15'],
);

export const LADR_MAIS_LADOS_MELHOR = define(
  'LADR.MAIS_LADOS_MELHOR',
  'concepcao',
  'Achou que mais lados pavimentam melhor',
  'Marcou polígonos de muitos lados, como o octógono ou o dodecágono. Quanto mais lados, maior o ângulo interno — e mais difícil ele caber um número inteiro de vezes em 360°.',
  ['H2.15'],
);

export const LADR_TODOS_REGULARES = define(
  'LADR.TODOS_REGULARES',
  'concepcao',
  'Achou que todo polígono regular pavimenta',
  'Marcou todos. Ser regular garante lados e ângulos iguais, não que o ângulo caiba um número inteiro de vezes em volta de um ponto.',
  ['H2.15'],
);

export const LADR_SO_ANGULO_RETO = define(
  'LADR.SO_ANGULO_RETO',
  'concepcao',
  'Achou que só o quadrado pavimenta',
  'Marcou apenas o quadrado, como se fosse preciso ângulo reto. O que importa é o ângulo caber em 360° — e 60° e 120° também cabem.',
  ['H2.15'],
);

export const LADR_TRIANGULO_DE_FORA = define(
  'LADR.TRIANGULO_DE_FORA',
  'concepcao',
  'Deixou o triângulo de fora',
  'Não marcou o triângulo equilátero, talvez por a ponta ser fina. Seis triângulos de 60° fecham a volta exatamente.',
  ['H2.15'],
);

// ---------------------------------------------------------------------------
// Probabilidade -- MODIFICAR a composicao para o evento ter a chance pedida (H1.32)
//
// Eixo cognitivo III. O aluno nao calcula a chance de um sorteio dado: ele mexe no
// sorteio ate a chance ser a pedida. Todos os erros daqui sao sobre a mesma armadilha --
// acrescentar bolas de uma cor faz crescer o numerador E o denominador --, e cada um
// nomeia uma maneira diferente de esquecer isso.
// ---------------------------------------------------------------------------

export const PROB_IGUALOU_AS_CORES = define(
  'PROB.IGUALOU_AS_CORES',
  'concepcao',
  'Igualou as quantidades das duas cores',
  'Deixou as duas cores com a mesma quantidade. Isso dá meio a meio, e não a fração pedida.',
  ['H1.32'],
);

export const PROB_TOTAL_DE_HOJE = define(
  'PROB.TOTAL_DE_HOJE',
  'concepcao',
  'Aplicou a fração ao total de agora',
  'Calculou a fração sobre o total que a urna tem hoje, esquecendo que as bolas acrescentadas também entram no total.',
  ['H1.32'],
);

export const PROB_SEM_DESCONTAR = define(
  'PROB.SEM_DESCONTAR',
  'procedimento',
  'Esqueceu as escuras que já estavam lá',
  'Acrescentou a quantidade inteira que o evento precisa ter, sem descontar as bolas escuras que a urna já tinha.',
  ['H1.32'],
);

export const PROB_RAZAO_POR_FRACAO = define(
  'PROB.RAZAO_POR_FRACAO',
  'formula-correlata',
  'Leu a fração como razão entre as cores',
  'Tratou "2 em 3" como "2 escuras para cada 3 claras". A fração é sobre o TOTAL de bolas, e não sobre a outra cor.',
  ['H1.32'],
);

export const PROB_TROCOU_A_COR = define(
  'PROB.TROCOU_A_COR',
  'leitura-suporte',
  'Resolveu para a outra cor',
  'Ajustou a urna para que a fração pedida fosse a das bolas claras, e a pergunta era sobre as escuras.',
  ['H1.32'],
);

// ---------------------------------------------------------------------------
// Eventos no lançamento de dois dados (H1.30.K01)
// ---------------------------------------------------------------------------

export const DADOS_ORDEM = define(
  'DADOS.ORDEM',
  'concepcao',
  'Ignorou a ordem dos dados',
  'Tratou (2, 5) e (5, 2) como o mesmo resultado. Com dois dados distintos, são dois resultados diferentes: num, o primeiro dado mostra 2; no outro, mostra 5.',
  ['H1.30', 'H1.28'],
);

export const DADOS_FRONTEIRA = define(
  'DADOS.FRONTEIRA',
  'concepcao',
  'Confundiu "maior que" com "maior ou igual a"',
  'Incluiu os resultados em que a soma é exatamente o número do enunciado — que "maior que" e "menor que" deixam de fora.',
  ['H1.30', 'H1.28'],
);

export const DADOS_PRODUTO_AMBOS_PARES = define(
  'DADOS.PRODUTO_AMBOS_PARES',
  'concepcao',
  'Achou que o produto só é par com os dois números pares',
  'Basta um dos fatores ser par para o produto ser par: 3 × 4 = 12.',
  ['H1.30', 'H1.28'],
);

export const DADOS_PRODUTO_UM_PAR = define(
  'DADOS.PRODUTO_UM_PAR',
  'concepcao',
  'Esqueceu que par vezes par também é par',
  'Marcou os resultados com exatamente um número par e deixou de fora os que têm os dois pares, como 2 × 4 = 8.',
  ['H1.30', 'H1.28'],
);

export const DADOS_EXCLUIU_DUPLO = define(
  'DADOS.EXCLUIU_DUPLO',
  'concepcao',
  'Leu "pelo menos um" como "exatamente um"',
  'Deixou de fora o resultado em que os dois dados mostram o número pedido — que também tem pelo menos um.',
  ['H1.30', 'H1.28'],
);

export const DADOS_UM_DADO_SO = define(
  'DADOS.UM_DADO_SO',
  'parada-precoce',
  'Olhou para um dado só',
  'Marcou apenas os resultados em que um dos dados — sempre o mesmo — mostra o número, esquecendo os casos em que é o outro dado que o mostra.',
  ['H1.30', 'H1.28'],
);

// ---------------------------------------------------------------------------
// Planificações do cubo (H3.17.K01)
// ---------------------------------------------------------------------------

export const CUBO_BLOCO_2X2 = define(
  'CUBO.BLOCO_2X2',
  'concepcao',
  'Aceitou uma figura com um bloco 2 × 2',
  'Quatro quadrados formando um quadrado maior nunca estão numa planificação do cubo: ao dobrar, dois deles caem na mesma face.',
  ['H3.17', 'H1.17'],
);

export const CUBO_CINCO_EM_LINHA = define(
  'CUBO.CINCO_EM_LINHA',
  'concepcao',
  'Aceitou cinco quadrados em fila',
  'Uma fila de quatro quadrados já dá a volta no cubo; o quinto cai de novo sobre o primeiro.',
  ['H3.17', 'H1.17'],
);

export const CUBO_FACES_SOBREPOSTAS = define(
  'CUBO.FACES_SOBREPOSTAS',
  'concepcao',
  'Aceitou uma figura em que duas faces se sobrepõem',
  'A figura tem seis quadrados, mas, ao dobrar, dois deles caem no mesmo lugar e uma face do cubo fica aberta.',
  ['H3.17', 'H1.17'],
);

export const CUBO_REJEITOU_VALIDA = define(
  'CUBO.REJEITOU_VALIDA',
  'concepcao',
  'Descartou uma planificação que fecha o cubo',
  'Há planificações do cubo além da cruz: figuras em escada, ou com duas filas de três, também fecham.',
  ['H3.17', 'H1.17'],
);

// ---------------------------------------------------------------------------
// Distância num mapa com escala (H1.24.K01)
// ---------------------------------------------------------------------------

export const ESC_MEDIDA_MAPA = define(
  'ESC.MEDIDA_MAPA',
  'parada-precoce',
  'Respondeu a medida no mapa',
  'Mediu a distância no mapa e respondeu esse número, sem usar a escala para chegar à distância real.',
  ['H1.24', 'H1.21'],
);

export const ESC_BARRA_INTEIRA = define(
  'ESC.BARRA_INTEIRA',
  'leitura-suporte',
  'Leu a barra de escala inteira como 1 cm',
  'O número escrito na ponta da barra de escala vale para o comprimento todo da barra, e não para cada centímetro dela.',
  ['H1.24', 'H1.21'],
);

export const ESC_CONVERSAO = define(
  'ESC.CONVERSAO',
  'procedimento',
  'Errou a conversão de centímetros para quilômetros',
  'Na escala 1 : N, 1 cm do mapa são N cm na realidade; para chegar a quilômetros, divide-se por 100.000, porque 1 km = 1.000 m = 100.000 cm.',
  ['H1.24', 'H1.21'],
);

export const ESC_REGUA_NO_UM = define(
  'ESC.REGUA_NO_UM',
  'leitura-suporte',
  'Começou a medir no 1 da régua',
  'Alinhou o ponto de partida com a marca do 1, e não com a do 0, e leu o número na chegada: a medida fica 1 cm maior.',
  ['H1.24', 'H1.21'],
);

// ---------------------------------------------------------------------------
// Ampliação e redução na malha (H1.22.K01)
// ---------------------------------------------------------------------------

export const AMPL_RAZAO_ERRADA = define(
  'AMPL.RAZAO_ERRADA',
  'procedimento',
  'Usou outra razão',
  'A figura ficou semelhante à original, mas as medidas foram multiplicadas por um número diferente do pedido.',
  ['H1.22'],
);

export const AMPL_UMA_DIRECAO = define(
  'AMPL.UMA_DIRECAO',
  'concepcao',
  'Ampliou numa direção só',
  'Multiplicou as medidas horizontais e deixou as verticais como estavam (ou o contrário): a figura esticou e deixou de ser semelhante à original.',
  ['H1.22'],
);

export const AMPL_ADITIVO = define(
  'AMPL.ADITIVO',
  'concepcao',
  'Somou em vez de multiplicar',
  'Acrescentou a mesma quantidade a todas as medidas. Numa ampliação, cada medida é multiplicada pela razão; somar o mesmo número a todas deforma a figura.',
  ['H1.22'],
);

export const AMPL_ORIENTACAO = define(
  'AMPL.ORIENTACAO',
  'procedimento',
  'Virou a figura',
  'O tamanho está certo, mas a figura foi virada ou girada. A ampliação pedida mantém a posição da original.',
  ['H1.22'],
);

export const AMPL_INCOMPLETO = define(
  'AMPL.INCOMPLETO',
  'parada-precoce',
  'Acertou só parte dos vértices',
  'Um dos vértices está no lugar certo, o outro não: as duas medidas precisam ser multiplicadas pela mesma razão.',
  ['H1.22'],
);

export const ERROR_CATALOG: Readonly<Record<string, ErrorAnchor>> = Object.freeze(
  Object.fromEntries(anchors.map((a) => [a.code as string, a])),
);

export function lookupError(code: ErrorCode): ErrorAnchor {
  const a = ERROR_CATALOG[code as string];
  if (!a) throw new Error(`erro nao catalogado: ${code}`);
  return a;
}

export function familyOf(code: ErrorCode): ErrorFamily {
  return lookupError(code).family;
}
